/**
 * GeometryViewerPage Component
 *
 * Page component for the 3D geometry viewer with editing capabilities (Epic 2.3).
 * Provides visualization and editing tools for building geometry.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIDFStore } from '../stores/idfStore';
import { useGeometryStore } from '../geometry/geometryStore';
import {
  useGeometryEditStore,
  useIsEditing,
  useEditMode,
  useNotifications,
} from '../geometry/geometryEditStore';
import {
  GeometryViewer,
  ViewerToolbar,
  ZoneCreationWizard,
  FloorPlanEditor,
  FenestrationPlacer,
  AdjacencyDetector,
  ConstructionBuilder,
  EditingToolbar,
  PropertiesPanel,
} from '../geometry/components';
import { FileRequiredEmptyState } from './FileRequiredEmptyState';
import type { CameraPreset } from '../geometry/types';

/**
 * Error boundary for catching render errors.
 */
class GeometryErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex h-full w-full items-center justify-center bg-destructive/10 p-8">
          <div className="max-w-lg rounded-lg bg-card p-6 shadow-lg">
            <h2 className="mb-4 text-xl font-bold text-destructive">
              3D Viewer Error
            </h2>
            <p className="mb-4 text-foreground">
              An error occurred while rendering the 3D viewer:
            </p>
            <pre className="mb-4 max-h-40 overflow-auto rounded bg-muted p-3 text-xs text-destructive">
              {this.state.error?.message}
              {'\n\n'}
              {this.state.error?.stack}
            </pre>
            <button
              onClick={() => this.setState({ hasError: false, error: null })}
              className="rounded bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90"
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * View mode for the page.
 */
type PageViewMode = '3d' | '2d-plan' | 'adjacency';

export function GeometryViewerPage() {
  const navigate = useNavigate();
  const document = useIDFStore((state) => state.document);
  const selectObject = useIDFStore((state) => state.selectObject);

  const geometry = useGeometryStore((state) => state.geometry);
  const selectedIds = useGeometryStore((state) => state.selectedIds);
  const viewMode = useGeometryStore((state) => state.viewMode);
  const colorScheme = useGeometryStore((state) => state.colorScheme);
  const showNormals = useGeometryStore((state) => state.showNormals);
  const showGrid = useGeometryStore((state) => state.showGrid);
  const showAxes = useGeometryStore((state) => state.showAxes);
  const isLoading = useGeometryStore((state) => state.isLoading);
  const error = useGeometryStore((state) => state.error);

  const extractFromDocument = useGeometryStore((state) => state.extractFromDocument);
  const setSelectedIds = useGeometryStore((state) => state.setSelectedIds);
  const clearSelection = useGeometryStore((state) => state.clearSelection);
  const setViewMode = useGeometryStore((state) => state.setViewMode);
  const setColorScheme = useGeometryStore((state) => state.setColorScheme);
  const setShowNormals = useGeometryStore((state) => state.setShowNormals);
  const setShowGrid = useGeometryStore((state) => state.setShowGrid);
  const setShowAxes = useGeometryStore((state) => state.setShowAxes);
  const setCameraPreset = useGeometryStore((state) => state.setCameraPreset);

  // Editing state
  const isEditing = useIsEditing();
  useEditMode(); // Keep the hook subscribed
  const notifications = useNotifications();
  const undo = useGeometryEditStore((state) => state.undo);
  const redo = useGeometryEditStore((state) => state.redo);
  const canUndo = useGeometryEditStore((state) => state.canUndo);
  const canRedo = useGeometryEditStore((state) => state.canRedo);
  const removeNotification = useGeometryEditStore((state) => state.removeNotification);
  const setSelection = useGeometryEditStore((state) => state.setSelection);
  const zoneWizard = useGeometryEditStore((state) => state.zoneWizard);
  const constructionBuilder = useGeometryEditStore((state) => state.constructionBuilder);
  const fenestrationPlacer = useGeometryEditStore((state) => state.fenestrationPlacer);

  // Page view mode
  const [pageViewMode, setPageViewMode] = useState<PageViewMode>('3d');

  // Properties panel state
  const [propertiesPanelCollapsed, setPropertiesPanelCollapsed] = useState(false);

  // Camera preset tracking for active state
  const currentPreset = useGeometryStore((state) => state.currentPreset);

  // Camera control triggers (increment to trigger actions)
  const [fitAllTrigger, setFitAllTrigger] = useState(0);
  const [zoomToSelectionTrigger, setZoomToSelectionTrigger] = useState(0);
  const [activeCameraPreset, setActiveCameraPreset] = useState<CameraPreset | undefined>(undefined);

  // Track if we've already extracted for this document
  const lastDocumentRef = useRef<typeof document>(null);

  // Extract geometry when document changes
  useEffect(() => {
    if (document && document !== lastDocumentRef.current) {
      lastDocumentRef.current = document;
      extractFromDocument(document);
    }
  }, [document, extractFromDocument]);

  // Re-extract geometry when IDF is modified (to reflect editing changes)
  const isDirty = useIDFStore((state) => state.isDirty);
  useEffect(() => {
    if (document && isDirty) {
      // Debounce the re-extraction
      const timer = setTimeout(() => {
        extractFromDocument(document);
      }, 500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [document, isDirty, extractFromDocument]);

  // Handle selection
  const handleSelect = useCallback(
    (ids: string[]) => {
      setSelectedIds(ids);
      // Sync with editing store
      setSelection({ surfaceIds: ids });
      // Also select in the IDF store for editor sync
      if (ids.length === 1 && ids[0]) {
        selectObject(ids[0]);
      } else if (ids.length === 0) {
        selectObject(null);
      }
    },
    [setSelectedIds, setSelection, selectObject]
  );

  // Handle camera preset
  const handleCameraPreset = useCallback(
    (preset: CameraPreset) => {
      setCameraPreset(preset);
      setActiveCameraPreset(preset);
    },
    [setCameraPreset]
  );

  // Handle fit all
  const handleFitAll = useCallback(() => {
    setFitAllTrigger((prev) => prev + 1);
    setActiveCameraPreset('isometric');
    setCameraPreset('isometric');
  }, [setCameraPreset]);

  // Handle zoom to selection
  const handleZoomToSelection = useCallback(() => {
    if (selectedIds.length > 0 && geometry) {
      setZoomToSelectionTrigger((prev) => prev + 1);
    }
  }, [selectedIds, geometry]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Undo/Redo
      if ((event.ctrlKey || event.metaKey) && event.key === 'z') {
        event.preventDefault();
        if (event.shiftKey) {
          if (canRedo) redo();
        } else {
          if (canUndo) undo();
        }
        return;
      }
      if ((event.ctrlKey || event.metaKey) && event.key === 'y') {
        event.preventDefault();
        if (canRedo) redo();
        return;
      }

      // Home key: fit all
      if (event.key === 'Home') {
        event.preventDefault();
        handleFitAll();
      }
      // Escape: clear selection
      if (event.key === 'Escape') {
        clearSelection();
      }
      // G: toggle grid
      if (event.key === 'g' || event.key === 'G') {
        if (!event.ctrlKey && !event.metaKey) {
          setShowGrid(!showGrid);
        }
      }
      // N: toggle normals
      if (event.key === 'n' || event.key === 'N') {
        if (!event.ctrlKey && !event.metaKey) {
          setShowNormals(!showNormals);
        }
      }
      // W: wireframe mode
      if (event.key === 'w' || event.key === 'W') {
        if (!event.ctrlKey && !event.metaKey) {
          setViewMode('wireframe');
        }
      }
      // S: shaded mode (only when not typing)
      if (event.key === 's' || event.key === 'S') {
        if (!event.ctrlKey && !event.metaKey && globalThis.document.activeElement?.tagName !== 'INPUT') {
          setViewMode('shaded');
        }
      }
      // X: x-ray mode
      if (event.key === 'x' || event.key === 'X') {
        if (!event.ctrlKey && !event.metaKey) {
          setViewMode('x-ray');
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    handleFitAll,
    clearSelection,
    showGrid,
    setShowGrid,
    showNormals,
    setShowNormals,
    setViewMode,
    undo,
    redo,
    canUndo,
    canRedo,
  ]);

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-muted">
        <div className="text-center">
          <div className="mb-4 h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-muted-foreground">Extracting geometry...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-muted">
        <div className="max-w-md rounded-lg bg-destructive/10 p-6 text-center">
          <p className="mb-2 text-lg font-medium text-destructive">Error</p>
          <p className="text-destructive/80">{error}</p>
        </div>
      </div>
    );
  }

  // Show message if no document loaded
  if (!document) {
    return (
      <FileRequiredEmptyState
        reason="no-file"
        title="No Model Loaded"
        message="Open an IDF file in the Editor to view its 3D geometry."
        action={{
          label: 'Go to Editor',
          onClick: () => navigate('/'),
        }}
      />
    );
  }

  return (
    <GeometryErrorBoundary>
      <div className="flex h-full w-full flex-col">
        {/* Editing Toolbar (shown when in edit mode or always for easy access) */}
        <EditingToolbar
        onOpenZoneWizard={() => {}}
        onOpenFloorPlan={() => setPageViewMode('2d-plan')}
        onOpenAdjacencyDetector={() => setPageViewMode('adjacency')}
        onOpenConstructionBuilder={() => {}}
      />

      {/* Main content area */}
      <div className="flex min-h-0 flex-1">
        {/* Main view */}
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
          {/* View mode tabs - always visible for easy navigation */}
          <div className="flex border-b border-border bg-muted/50" role="tablist" aria-label="View modes">
            <button
              onClick={() => setPageViewMode('3d')}
              role="tab"
              aria-selected={pageViewMode === '3d'}
              aria-controls="panel-3d"
              className={`px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary ${
                pageViewMode === '3d'
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              3D View
            </button>
            <button
              onClick={() => setPageViewMode('2d-plan')}
              role="tab"
              aria-selected={pageViewMode === '2d-plan'}
              aria-controls="panel-2d"
              className={`px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary ${
                pageViewMode === '2d-plan'
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              2D Plan
            </button>
            <button
              onClick={() => setPageViewMode('adjacency')}
              role="tab"
              aria-selected={pageViewMode === 'adjacency'}
              aria-controls="panel-adjacency"
              className={`px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary ${
                pageViewMode === 'adjacency'
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Adjacency
            </button>
            {/* Spacer */}
            <div className="flex-1" />
            {/* Mobile/Touch help */}
            <div className="hidden items-center gap-2 px-4 text-xs text-muted-foreground md:flex">
              <span>Pinch to zoom</span>
              <span className="text-border">|</span>
              <span>Two-finger drag to pan</span>
            </div>
          </div>

          {/* Viewer Toolbar (for 3D view) */}
          {pageViewMode === '3d' && (
            <ViewerToolbar
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              colorScheme={colorScheme}
              onColorSchemeChange={setColorScheme}
              showNormals={showNormals}
              onShowNormalsChange={setShowNormals}
              showGrid={showGrid}
              onShowGridChange={setShowGrid}
              showAxes={showAxes}
              onShowAxesChange={setShowAxes}
              onCameraPreset={handleCameraPreset}
              onFitAll={handleFitAll}
              onZoomToSelection={selectedIds.length > 0 ? handleZoomToSelection : undefined}
              selectedCount={selectedIds.length}
              onClearSelection={clearSelection}
              activeCameraPreset={currentPreset ?? undefined}
            />
          )}

          {/* 3D Viewer */}
          {pageViewMode === '3d' && (
            <div id="panel-3d" role="tabpanel" aria-labelledby="tab-3d" className="min-h-0 flex-1" data-onboarding="geometry-canvas">
              <GeometryViewer
                geometry={geometry}
                selectedIds={selectedIds}
                onSelect={handleSelect}
                viewMode={viewMode}
                colorScheme={colorScheme}
                showNormals={showNormals}
                showGrid={showGrid}
                showAxes={showAxes}
                cameraPreset={activeCameraPreset}
                fitAllTrigger={fitAllTrigger}
                zoomToSelectionTrigger={zoomToSelectionTrigger}
              />
            </div>
          )}

          {/* 2D Floor Plan Editor */}
          {pageViewMode === '2d-plan' && (
            <div id="panel-2d" role="tabpanel" aria-labelledby="tab-2d" className="min-h-0 flex-1">
              <FloorPlanEditor
                onFootprintComplete={(vertices) => {
                  console.log('Footprint complete:', vertices);
                }}
              />
            </div>
          )}

          {/* Adjacency Detector Panel */}
          {pageViewMode === 'adjacency' && (
            <div id="panel-adjacency" role="tabpanel" aria-labelledby="tab-adjacency" className="min-h-0 flex-1">
              <AdjacencyDetector onClose={() => setPageViewMode('3d')} />
            </div>
          )}
        </div>

        {/* Properties Panel - shows on 3D view */}
        {pageViewMode === '3d' && (
          <PropertiesPanel
            isCollapsed={propertiesPanelCollapsed}
            onToggleCollapse={() => setPropertiesPanelCollapsed(!propertiesPanelCollapsed)}
          />
        )}
      </div>

      {/* Status bar */}
      <div className="flex items-center justify-between border-t border-border bg-muted/50 px-4 py-1 text-xs text-muted-foreground">
        <div role="status" aria-live="polite">
          {geometry ? (
            <>
              <span className="font-medium">{geometry.zones.length}</span> zones |{' '}
              <span className="font-medium">{geometry.zones.reduce((acc, z) => acc + z.surfaces.length, 0)}</span> surfaces |{' '}
              <span className="font-medium">
                {geometry.zones.reduce(
                  (acc, z) => acc + z.surfaces.reduce((a, s) => a + s.fenestrations.length, 0),
                  0
                )}
              </span>{' '}
              fenestrations
            </>
          ) : (
            'No geometry'
          )}
        </div>
        <div className="flex items-center gap-4">
          {isEditing && (
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-primary">
              Edit Mode
            </span>
          )}
          {/* Desktop controls */}
          <span className="hidden text-muted-foreground/70 md:inline">
            Orbit: RMB | Pan: Shift+RMB | Zoom: Scroll | Select: Click | Multi: Ctrl+Click | Zoom to: Double-click
          </span>
          {/* Mobile/Touch controls */}
          <span className="inline text-muted-foreground/70 md:hidden">
            Pinch: Zoom | 2-finger: Pan | Tap: Select
          </span>
        </div>
      </div>

      {/* Modals and floating panels */}
      {zoneWizard.isOpen && (
        <ZoneCreationWizard
          onOpenFloorPlan={() => setPageViewMode('2d-plan')}
        />
      )}
      {constructionBuilder.isOpen && <ConstructionBuilder />}
      {fenestrationPlacer.isPlacing && <FenestrationPlacer />}

      {/* Notifications */}
      <div className="fixed bottom-20 right-2 left-2 md:left-auto md:right-4 z-50 space-y-2">
        {notifications.map((notification) => (
          <div
            key={notification.id}
            className={`flex items-center gap-3 rounded-lg px-4 py-3 shadow-lg ${
              notification.type === 'success'
                ? 'bg-green-50 text-green-800 dark:bg-green-950 dark:text-green-200'
                : notification.type === 'error'
                  ? 'bg-red-50 text-red-800 dark:bg-red-950 dark:text-red-200'
                  : notification.type === 'warning'
                    ? 'bg-yellow-50 text-yellow-800 dark:bg-yellow-950 dark:text-yellow-200'
                    : 'bg-blue-50 text-blue-800 dark:bg-blue-950 dark:text-blue-200'
            }`}
          >
            <span className="text-sm">{notification.message}</span>
            <button
              onClick={() => removeNotification(notification.id)}
              className="text-muted-foreground hover:text-foreground"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        ))}
      </div>
      </div>
    </GeometryErrorBoundary>
  );
}
